import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  Tooltip,
  Switch,
  FormControlLabel,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ContractorCard from "../../permitComponents/ContractorCard";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { useSelector } from "react-redux";
import { useMemo, useState, useEffect } from "react";
import { useTour } from "@reactour/tour";
import { useNavigate } from "react-router-dom";
import {
  getCanonicalDocumentKey,
  isSpecialForm,
} from "../../permitForms/specialFormsMap";

const SelectContractorStep = ({
  contractorIndex,
  setContractorIndex,
  page,
  selectedCity,
  selectedForms,
  availableForms,
  county,
  displayImage,
  cities,
  contractors,
  isPP,
  favoritesOnly,
  setFavoritesOnly,
  onCardsScroll,
  setShouldExpandOnScroll,
  descriptionTextRef,
  projectNameFieldRef,
}) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const { setCurrentStep } = useTour();
  const navigate = useNavigate();

  const theme = useTheme();
  const isMediumDown = useMediaQuery(theme.breakpoints.down("md"));

  const [scrollSectionHeight, setScrollSectionHeight] = useState(0);

  const availableFormsNum = useMemo(() => {
    const countedSpecialFormKeys = new Set();

    return (availableForms || []).reduce((total, form) => {
      if (!form) return total;

      if (!isSpecialForm(form)) {
        return total + 1;
      }

      const canonicalKey = getCanonicalDocumentKey(form);
      if (!canonicalKey || countedSpecialFormKeys.has(canonicalKey)) {
        return total;
      }

      countedSpecialFormKeys.add(canonicalKey);
      return total + 1;
    }, 0);
  }, [availableForms]);

  useEffect(() => {
    const updateHeight = () => {
      const height =
        (isMediumDown ? 310 : 250) +
        (descriptionTextRef?.current?.offsetHeight ?? 0) +
        (projectNameFieldRef?.current?.offsetHeight ?? 0);
      setScrollSectionHeight(`calc(100vh - ${height}px)`);
    };

    const observer = new ResizeObserver(updateHeight);

    if (descriptionTextRef?.current)
      observer.observe(descriptionTextRef.current);
    if (projectNameFieldRef?.current)
      observer.observe(projectNameFieldRef.current);

    updateHeight();

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contractorKey = (c) =>
    c?.id ?? c?.licenseNumber ?? c?.license_number ?? c?._id ?? "";

  const contractorLabel = (c) => {
    if (!c) return "";
    const name =
      c.dba_name ||
      c.businessName ||
      [c.firstName ?? c.first_name, c.lastName ?? c.last_name]
        .filter(Boolean)
        .join(" ") ||
      c.primary_name ||
      "";
    const lic = c.licenseNumber ?? c.license_number ?? "";
    const county = c.county ?? c.mainAddressCounty ?? "";
    return [name, lic, county].filter(Boolean).join(" — ");
  };

  // Favorites (PP-only)
  const FAVORITES_KEY = "pp_contractor_favorites";
  const [favorites, setFavorites] = useState([]);

  const readFavorites = () => {
    try {
      const data = JSON.parse(localStorage.getItem(FAVORITES_KEY));
      return Array.isArray(data) ? data : [];
    } catch {
      return [];
    }
  };

  // Initialize favorites from localStorage on component mount
  useEffect(() => {
    setFavorites(readFavorites());
  }, []);

  const isFavorite = (c) => {
    if (!c) return false;
    const key = contractorKey(c);
    return favorites.some((f) => contractorKey(f) === key);
  };

  const toggleFavorite = (c) => {
    if (!c) return;
    const key = contractorKey(c);
    const idx = favorites.findIndex((f) => contractorKey(f) === key);
    let next;
    if (idx >= 0) {
      // Remove from favorites
      next = [...favorites.slice(0, idx), ...favorites.slice(idx + 1)];
    } else {
      // Add to favorites - store a compact version
      const compact = {
        id: c.id ?? c._id ?? null,
        licenseNumber: c.licenseNumber ?? c.license_number ?? "",
        businessName: c.businessName ?? c.dba_name ?? c.primary_name ?? "",
        firstName: c.firstName ?? c.first_name ?? "",
        lastName: c.lastName ?? c.last_name ?? "",
        county: c.mainAddressCounty ?? c.county ?? "",
      };
      next = [compact, ...favorites];
    }
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(next));
    setFavorites(next); // Update local state to trigger re-render
  };

  const visibleContractors = useMemo(() => {
    if (!isPP) return contractors ?? [];
    if (!favoritesOnly) return contractors ?? [];
    const favs = favorites;
    const favKeys = new Set(favs.map((f) => contractorKey(f)));
    return (contractors ?? []).filter((c) => favKeys.has(contractorKey(c)));
  }, [contractors, isPP, favoritesOnly, favorites]);

  // Update filteredContractorIndex when favoritesOnly changes
  useEffect(() => {
    if (isPP && favoritesOnly) {
      const currentContractor = contractors?.[contractorIndex];
      if (currentContractor) {
        const favKeys = new Set(favorites.map((f) => contractorKey(f)));
        const isInFavorites = favKeys.has(contractorKey(currentContractor));

        if (!isInFavorites) {
          setContractorIndex(0);
        }
      }
    }
  }, [
    favoritesOnly,
    favorites,
    contractors,
    contractorIndex,
    isPP,
    setContractorIndex,
  ]);

  // Ensure contractorIndex is valid when visibleContractors changes
  useEffect(() => {
    if (isPP && visibleContractors.length > 0) {
      const currentContractor = contractors?.[contractorIndex];
      if (currentContractor) {
        const isVisible = visibleContractors.some(
          (c) => contractorKey(c) === contractorKey(currentContractor),
        );
        if (!isVisible) {
          setContractorIndex(0);
        }
      }
    }
  }, [
    visibleContractors,
    contractors,
    contractorIndex,
    isPP,
    setContractorIndex,
  ]);

  const handleNavigate = (direction) => {
    let newPage;
    if (direction === "forward") {
      newPage = contractorIndex + 1;
    } else if (direction === "back") {
      newPage = contractorIndex - 1;
    }

    setContractorIndex(newPage);
  };
  return (
    <>
      <Box
        sx={{
          borderBottom: "3px solid #d3d3d3",
          mb: 0.5,
        }}
      />

      {page === 1 && contractors?.length === 0 && (
        <>
          <Typography align="center" variant="h6" mb={2}>
            {currentSession === "subUserSession"
              ? "Please contact your administrator to add a new contractor"
              : "You do not have any contractors eligible for the selected permit type. Please add a new contractor or choose a different permit type."}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            {currentSession === "userSession" && (
              <Button
                onClick={() => {
                  setTimeout(() => {
                    setCurrentStep(6);
                  }, 200);
                  navigate(
                    currentSession === "privateProviderSession"
                      ? "/privateProvider/contractors"
                      : "/contractors",
                    {
                      state: {
                        fromPermit: true,
                        selectedCity: selectedCity,
                        selectedForms: selectedForms,
                        availableForms: availableForms,
                        county: county,
                        displayImage: displayImage,
                        availableFormsNum: availableFormsNum,
                        cities: cities,
                      },
                    },
                  );
                }}
                variant="contained"
              >
                Add Contractor
              </Button>
            )}
          </Box>
        </>
      )}
      {contractors?.length > 0 && (
        <Box
          sx={{
            height: scrollSectionHeight,
            overflowY: "auto",
          }}
          onScroll={(event) => {
            if (!onCardsScroll) return;
            const shouldCondense = event.currentTarget.scrollTop > 20;
            onCardsScroll(shouldCondense);
            if (isMediumDown) {
              setShouldExpandOnScroll(shouldCondense);
            }
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: isPP ? 2 : undefined,
              my: isPP ? 1 : undefined,
            }}
          >
            {!isPP && (
              <>
                <IconButton
                  disabled={
                    contractorIndex === 0 || contractors?.length <= 1
                      ? true
                      : false
                  }
                  onClick={() => handleNavigate("back")}
                >
                  <ArrowBackIosNewIcon
                    sx={{
                      fontSize: "40px",
                      color:
                        contractorIndex === 0 || contractors?.length <= 1
                          ? "#d3d3d3"
                          : "#004976",
                    }}
                  />
                </IconButton>
                <Typography align="center" variant="h5" mx={1}>
                  {contractorIndex + 1 + "/" + contractors?.length}
                </Typography>
                <IconButton
                  disabled={
                    contractors?.length <= 0 ||
                    contractorIndex + 1 === contractors?.length
                      ? true
                      : false
                  }
                  onClick={() => handleNavigate("forward")}
                >
                  <ArrowForwardIosIcon
                    sx={{
                      fontSize: "40px",
                      color:
                        contractors?.length <= 0 ||
                        contractorIndex + 1 === contractors?.length
                          ? "#d3d3d3"
                          : "#004976",
                    }}
                  />
                </IconButton>
              </>
            )}

            {isPP && (
              <Box sx={{ width: "100%", maxWidth: "500px" }}>
                <Autocomplete
                  fullWidth
                  options={visibleContractors ?? []}
                  value={contractors?.[contractorIndex] ?? null}
                  getOptionLabel={(opt) => contractorLabel(opt)}
                  isOptionEqualToValue={(opt, val) =>
                    contractorKey(opt) === contractorKey(val)
                  }
                  onChange={(_, val) => {
                    if (!val) return;
                    const idx = (contractors ?? []).findIndex(
                      (c) => contractorKey(c) === contractorKey(val),
                    );
                    if (idx >= 0) setContractorIndex(idx);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Contractor"
                      variant="filled"
                    />
                  )}
                  slotProps={{
                    listbox: {
                      style: {
                        maxHeight: "150px",
                        overflowY: "auto",
                      },
                    },
                  }}
                />
                <Box
                  sx={{
                    mt: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={favoritesOnly}
                        onChange={(e) => setFavoritesOnly(e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Favorites only"
                  />
                  <Tooltip
                    title="Toggle Favorite for selected contractor"
                    arrow
                  >
                    <IconButton
                      disabled={!contractors?.[contractorIndex]}
                      onClick={() =>
                        toggleFavorite(contractors?.[contractorIndex])
                      }
                      color={
                        isFavorite(contractors?.[contractorIndex])
                          ? "warning"
                          : "default"
                      }
                    >
                      {isFavorite(contractors?.[contractorIndex]) ? (
                        <StarIcon sx={{ color: "#ff9800" }} />
                      ) : (
                        <StarBorderIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
            <ContractorCard
              data={contractors?.[contractorIndex]}
              colorIndex={contractorIndex}
              empty={contractors.length === 0}
              showLicenseInput={false}
            />
          </Box>
        </Box>
      )}
    </>
  );
};

export default SelectContractorStep;
